import { createOemOfferFromIncentiveOffers } from 'components/PencilEditor/PencilOfferList/EditIncentives';
import { isDefined } from '@upstart/harmony/lib/legos/utils/tsutils';
import {
  getDealerCashRuleAppliedValue,
  getResidualValueFromPercent,
} from '@upstart/harmony/lib/payment-utils';
import {
  DealerCashRuleType,
  FinanceType,
} from '@upstart/harmony/lib/legos/constants';
import { get, set } from 'lodash';
import * as changeLogHelper from '@upstart/harmony/lib/deskingoffer-changelog';
import { IncentivesProgram } from '@upstart/harmony/lib/legos/carTypes';
import { getNewLeaseProgramsFromOemOffers } from 'components/PencilEditor/PencilOfferList/offer-utils';
import { RATE_FACTOR, RATE_TO_APR } from 'util/constants';
import { User } from '@upstart/harmony/lib/legos/userTypes';
import { isMarketScanIncentivesEnabled } from '@upstart/harmony/lib/dealer-utils';
import {
  CalculateAndSaveMoneyFactorArg,
  CalculateAndSaveRatePercentageArg,
  DeskingOfferCardProps,
  NormalizedOffer,
  RefreshResidualArg,
  UpdateAndLogPropertyArg,
  UpdateDealerCashRuleArg,
  UpdateMonthsMilesArg,
} from './types';
import * as AisDeskingOfferUtils from './desking-offer-utils-ais';
import * as MSDeskingOfferUtils from './desking-offer-utils-marketscan';

export function isOnePayLease({
  deal,
  dealer,
  offer,
}: Pick<DeskingOfferCardProps, 'offer' | 'deal' | 'dealer'>) {
  return (
    offer.data?.financeType === FinanceType.LEASE &&
    dealer?.desking?.enableOnePayLease &&
    deal?.onePayLease
  );
}

// getMrm will need separate implementations for AIS and MarketScan
export function getMrm({
  car,
  offer,
}: Pick<DeskingOfferCardProps, 'car' | 'offer'>) {
  return (offer?.data as any)?.mrm || car?.mrm || 0;
}

export function getDefaultResidualPercentage({
  car,
  dealer,
  offer,
}: Pick<DeskingOfferCardProps, 'car' | 'dealer' | 'offer'>):
  | number
  | undefined {
  if (isMarketScanIncentivesEnabled(dealer)) {
    // use the MS version
    return MSDeskingOfferUtils.getDefaultResidualPercentage();
  }

  // use the AIS version
  return AisDeskingOfferUtils.getDefaultResidualPercentage({
    car,
    offer,
  });
}
export function getResidualPercentage({
  car,
  dealer,
  offer,
}: Pick<DeskingOfferCardProps, 'car' | 'dealer' | 'offer'>) {
  if (isMarketScanIncentivesEnabled(dealer)) {
    // use the MS version
    return MSDeskingOfferUtils.getResidualPercentage({ offer });
  }

  // use the AIS version
  return AisDeskingOfferUtils.getResidualPercentage({ car, dealer, offer });
}

export function calculateDefaultCapitalizedTax({
  offer,
  dealer,
}: Pick<DeskingOfferCardProps, 'offer' | 'dealer'>) {
  if (offer.data?.financeType === FinanceType.LEASE) {
    const rate = get(dealer, 'desking.capitalizedTaxRate', 0);
    const onePayLease = (offer.data as any).onePayLease ?? false;
    const downPayment = onePayLease ? 0 : offer.data.downPayment ?? 0;
    return (rate * downPayment).toFixed(2);
  }
  return '0';
}

export const getOemOfferForOffer = ({
  car,
  offer,
}: Pick<DeskingOfferCardProps, 'car' | 'offer'>) => {
  const incentiveOffers = (car?.incentiveOffers || [])
    .filter((o: any) => o.financeTypes.includes(offer.data?.financeType))
    .filter(
      (incentiveOffer: any) =>
        (!incentiveOffer.from ||
          (offer.data?.financeType === FinanceType.PURCHASE &&
            offer.data.months >= incentiveOffer.from)) &&
        (!incentiveOffer.to ||
          (offer.data?.financeType === FinanceType.PURCHASE &&
            offer.data.months <= incentiveOffer.to)) &&
        (offer.data?.programs || []).find(
          (program: any) => program.id === incentiveOffer.id,
        ) !== undefined,
    );
  return createOemOfferFromIncentiveOffers(incentiveOffers);
};

const getFullNameFromCurrentUser = (currentUser?: Partial<User>) =>
  [currentUser?.firstName, currentUser?.lastName]
    .filter((e: string | undefined | null) => e)
    .join(' ');

export const logTradeInProperty = ({
  offer,
  propName,
  newValue,
  oldValue,
  currentUser,
}: UpdateAndLogPropertyArg) => {
  const changeMadeBy = getFullNameFromCurrentUser(currentUser);
  offer.changeLog = changeLogHelper.updateTradeInProperty({
    deskingOffer: offer,
    field: propName,
    newValue,
    oldValue,
    changeMadeBy,
  });
};

export const logProperty = ({
  offer,
  propName,
  newValue,
  oldValue,
  shouldLog,
  currentUser,
}: UpdateAndLogPropertyArg) => {
  if (!isDefined(shouldLog) || shouldLog) {
    // for cash offers, we need to log downPayment as deposit
    const propNameAdjusted =
      propName === 'downPayment' && offer.data?.financeType === FinanceType.CASH
        ? 'deposit'
        : propName;
    const changeMadeBy = getFullNameFromCurrentUser(currentUser);
    const newChangeLog = changeLogHelper.updateOfferProperty(
      offer,
      propNameAdjusted,
      newValue,
      oldValue,
      changeMadeBy,
    );
    set(offer, 'changeLog', newChangeLog);
  }
};

export const updateAndLogProperty = (props: UpdateAndLogPropertyArg) => {
  logProperty(props);
  set(props.offer, ['data', props.propName], props.newValue);
};

/**
 * Calculates the residuals based on either the incentives or the
 * car's residual value.
 * @param offer
 * @param term
 * @param mileage
 * @param car
 * @param dealer
 * @param shouldLog
 */
const refreshResidual = ({
  offer,
  term,
  mileage,
  car,
  dealer,
  shouldLog,
  currentUser,
}: RefreshResidualArg) => {
  const newOffer = { ...offer };
  // residuals in the offer are stored as percentages
  const offerResidualPercent = newOffer.data.programs?.find(
    (program: IncentivesProgram) => !!program.residuals,
  )?.residuals?.[`${term}`]?.[mileage];

  let newResidual = offerResidualPercent
    ? getResidualValueFromPercent(
        dealer,
        car,
        newOffer.data.mrm || car.mrm,
        offerResidualPercent,
        newOffer.data.msrp,
      )
    : get(car, ['residualValues', term, mileage]);

  newResidual = isFinite(newResidual) ? newResidual : 0;
  updateAndLogProperty({
    offer: newOffer,
    propName: 'residual',
    newValue: newResidual,
    oldValue: null,
    shouldLog,
    currentUser,
  });

  const newResidualPercent = getResidualPercentage({ car, dealer, offer });
  updateAndLogProperty({
    offer: newOffer,
    propName: 'residualPercentage',
    newValue: newResidualPercent,
    oldValue: null,
    shouldLog,
    currentUser,
  });
};

/**
 * This function should be called when either the months or the mileage of an
 * offer is changed. It updates several offer properties that are dependent
 * on these parameters.
 * @param offer
 * @param months
 * @param mileage
 * @param car
 * @param dealer
 * @param dealerPriceRules
 * @param shouldLog
 */
export const updateMonthsMiles = ({
  offer,
  months,
  mileage,
  car,
  dealer,
  dealerPriceRules,
  shouldLog,
  currentUser,
}: UpdateMonthsMilesArg) => {
  if (isMarketScanIncentivesEnabled(dealer)) {
    if (isDefined(months)) {
      set(offer, ['data', 'months'], months);
    }
    if (isDefined(mileage)) {
      set(offer, ['data', 'mileage'], mileage);
    }

    return;
  }

  let currentMileage = mileage;
  if (!months || !isFinite(months)) {
    // default to the current term
    months = offer.data.months;
  } else if (
    (offer.data.programs || []).length > 0 &&
    offer.data.financeType === FinanceType.PURCHASE
  ) {
    const cashPrograms = offer.data.programs.filter(
      (p: any) =>
        p.appliedApr === null && (p.appliedCash > 0 || p.appliedDealerCash > 0),
    );

    // We will only preserve Cash Programs only and we will ignore rate based incentives programs
    // The desking manager has to manually select the rate based incentives programs.
    updateAndLogProperty({
      offer,
      propName: 'programs',
      newValue: cashPrograms,
      oldValue: null,
      shouldLog,
      currentUser,
    });
  }

  // update term
  updateAndLogProperty({
    offer,
    propName: 'months',
    newValue: months,
    oldValue: null,
    shouldLog,
    currentUser,
  });

  // On a lease deal, if we detect a change in term
  if (offer.data.financeType === FinanceType.LEASE) {
    if (!currentMileage || !isFinite(currentMileage)) {
      currentMileage = offer.data.mileage;
    }
    updateAndLogProperty({
      offer,
      propName: 'mileage',
      newValue: currentMileage,
      oldValue: null,
      shouldLog,
      currentUser,
    });
    refreshResidual({
      offer,
      term: months ?? 0,
      mileage: currentMileage ?? 0,
      car,
      dealer,
      shouldLog,
      currentUser,
    });

    const creditRating = get(offer, 'data.creditRating');
    const {
      apr,
      programs,
      cash,
      dealerCash,
      residualPercentage,
    } = getNewLeaseProgramsFromOemOffers(
      dealer,
      dealerPriceRules,
      car,
      months as number,
      currentMileage as number,
      creditRating,
    );
    if (
      apr &&
      isFinite(apr) &&
      programs &&
      (cash || cash === 0) &&
      isFinite(cash) &&
      (dealerCash || dealerCash === 0) &&
      isFinite(dealerCash) &&
      residualPercentage &&
      isFinite(residualPercentage)
    ) {
      if (shouldLog) {
        const newChangeLog = changeLogHelper.updateOfferProperty(
          offer,
          'rate',
          apr,
        );
        set(offer, 'changeLog', newChangeLog);
        set(offer, ['data', 'rate'], apr * RATE_FACTOR);
      }

      updateAndLogProperty({
        offer,
        propName: 'programs',
        newValue: programs,
        oldValue: null,
        shouldLog,
        currentUser,
      });
      updateAndLogProperty({
        offer,
        propName: 'rebate',
        newValue: cash || 0,
        oldValue: null,
        shouldLog,
        currentUser,
      });
      updateAndLogProperty({
        offer,
        propName: 'dealerCash',
        newValue: dealerCash || 0,
        oldValue: null,
        shouldLog,
        currentUser,
      });

      // update residual
      const mrm = offer.data.mrm || car.mrm;
      const newResidual = getResidualValueFromPercent(
        dealer,
        car,
        mrm,
        residualPercentage,
        offer.data.msrp,
      );

      const residualPercentageDecimal = residualPercentage / 100;
      updateAndLogProperty({
        offer,
        propName: 'residual',
        newValue: newResidual,
        oldValue: null,
        shouldLog,
        currentUser,
      });
      updateAndLogProperty({
        offer,
        propName: 'residualPercentage',
        newValue: residualPercentageDecimal,
        oldValue: null,
        shouldLog,
        currentUser,
      });
    }
  }
};

/**
 * Multiplies rate percentage by rate factor to calculate Rate (Money Factor) and saves to DeskingOffer
 * @param offer
 * @param newRatePercentage
 * @param oldRatePercentage
 */
export const calculateAndSaveMoneyFactor = ({
  offer,
  propName,
  newRatePercentage,
  oldRatePercentage,
  shouldLog,
  currentUser,
}: CalculateAndSaveMoneyFactorArg) => {
  const moneyFactorRate = newRatePercentage * RATE_FACTOR;
  updateAndLogProperty({
    offer,
    propName,
    newValue: moneyFactorRate,
    oldValue: oldRatePercentage,
    shouldLog,
    currentUser,
  });
};

/**
 * Multiplies rate percentage by rate factor to calculate Rate (Money Factor) and saves to DeskingOffer
 * @param offer
 * @param newRate
 * @param oldRate
 */
export const calculateAndSaveRatePercentage = ({
  offer,
  newRate,
  oldRate,
  shouldLog,
  currentUser,
}: CalculateAndSaveRatePercentageArg) => {
  const ratePercentage = newRate * RATE_TO_APR;
  updateAndLogProperty({
    offer,
    propName: 'ratePercentage',
    newValue: ratePercentage,
    oldValue: oldRate,
    shouldLog,
    currentUser,
  });
};

export const updateDealerCashRule = ({
  oldOffer,
  newDealerCashRuleValue,
  offerToUpdate,
  currentUser,
}: UpdateDealerCashRuleArg): NormalizedOffer => {
  const dealerCash = oldOffer?.data?.dealerCash;
  const oldDealerCashRuleValue = getDealerCashRuleAppliedValue(oldOffer.data);
  const normalized = Math.min(dealerCash ?? 0, newDealerCashRuleValue ?? 0);
  const useFullDealerCash = normalized === dealerCash;
  const newChangeLog = changeLogHelper.updateDealerCashRule(
    offerToUpdate,
    normalized,
    oldDealerCashRuleValue,
    getFullNameFromCurrentUser(currentUser),
  );
  const newDealerCashRule = useFullDealerCash
    ? { type: DealerCashRuleType.PRICE_OFFSET_PERCENT, value: 100 }
    : { type: DealerCashRuleType.PRICE_OFFSET_FIXED_MAX, value: normalized };
  set(offerToUpdate, 'changeLog', newChangeLog);
  set(offerToUpdate, ['data', 'dealerCashRule'], newDealerCashRule);
  return offerToUpdate;
};

export function getAppliedPrograms(programs: IncentivesProgram[] | undefined) {
  if (!programs) {
    return [];
  }

  return programs.filter(
    (program: IncentivesProgram) =>
      program.appliedApr !== null ||
      program.appliedCash ||
      program.appliedDealerCash,
  );
}
